import React, { useRef, useEffect } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Container, Row, Col } from 'reactstrap';
import { IGetAllProjectsObject } from 'cd-eft/models/Projects/projectsLibrary';
import { ProjectCard } from '../ProjectCard/ProjectCard';
import { ProjectsLibraryVM } from './ProjectsLibraryVM';
import './projectsLibrary.scss';
import stores from 'cd-eft/models/Stores';
import { Pagination } from '../Pagination/Pagination';
import { Filter } from '../Filter/Filter';
import { filterProjects } from '../Filter/FilterVM';
import { AdvancedSearch } from '../AdvancedSearch/AdvancedSearch';
import Icon from '@mdi/react';
import { mdiClose } from '@mdi/js';

const appConfig = stores.AppStore.appConfig;
const libraryStore = stores.AppStore.projectsLibraryStore;

const ProjectsComponent = () => {
  const history = useHistory();
  const vm = useRef(new ProjectsLibraryVM(history)).current;
  const { pageNumber }: any = useParams();

  useEffect(() => {
    filterProjects();
  }, [
    libraryStore.filterString,
    libraryStore.projects.length,
    libraryStore.filterProjectTypes.size,
    libraryStore.filterProjectStatuses.size,
  ]);

  useEffect(() => {
    if (pageNumber && libraryStore.pageNumber !== Number(pageNumber)) libraryStore.pageNumber = Number(pageNumber);
  }, [pageNumber, libraryStore.pageNumber]);

  useEffect(() => {
    if (!pageNumber && libraryStore.pageNumber !== 1) {
      history.push(`/projects/page/${libraryStore.pageNumber}`);
    }
    if (Number(libraryStore.pageNumber - 1) * libraryStore.limitPerPage > libraryStore.filteredProjects.length) {
      history.push('/projects/page/1');
    }
  }, [pageNumber, libraryStore.pageNumber, libraryStore.limitPerPage, libraryStore.filteredProjects.length]);

  return (
    <div className="project-library">
      <Row>
        <Col md={12} lg={4}>
          <Row className="justify-content-between project-library-header">
            <Col>
              <h1 className="h2 nobr">Projects Library</h1>
            </Col>
            <Col className="nogrow">
              {appConfig.projectFlags.enableCreateProject ? (
                <Link to="/projects/new-project">
                  <div className="usa-button m-0 create-button nobr">Create Project</div>
                </Link>
              ) : (
                <button className="usa-button m-0" disabled>
                  Create Project
                </button>
              )}
            </Col>
          </Row>
        </Col>
        <Col>
          <Filter />
        </Col>
      </Row>

      {/* Applied Filters */}
      <div className="applied-filters mt-3">
        {libraryStore.filterString.length > 0 && (
          <AppliedFilterButton
            name={`"${libraryStore.filterString}"`}
            key="filter-string"
            onClick={vm.clearFilterString}
          />
        )}

        {libraryStore.sortBy > 0 && (
          <AppliedFilterButton
            name={libraryStore.sortByOptions[libraryStore.sortBy].replace(':', '')}
            key="filter-sortBy"
            onClick={vm.clearFilterSortBy}
          />
        )}

        {libraryStore.filterProjectTypes.size > 0 &&
          Array.from(libraryStore.filterProjectTypes).map((el: number) => (
            <AppliedFilterButton
              name={libraryStore.projectTypes[el]}
              key={`filter-type-${el}`}
              onClick={() => vm.clearFilterProjectType(el)}
            />
          ))}

        {libraryStore.filterProjectStatuses.size > 0 &&
          Array.from(libraryStore.filterProjectStatuses).map((el: number) => (
            <AppliedFilterButton
              name={
                libraryStore.projectStatuses[libraryStore.projectStatuses.findIndex((st) => st.id === el)].description
              }
              key={`filter-status-${el}`}
              onClick={() => vm.clearFilterProjectStatus(el)}
            />
          ))}
      </div>

      <hr />

      {/* ProjectCards */}
      <div className="pr-0">
        <div className="my-3 text-bold">Project Count: {libraryStore.filteredProjects.length}</div>
        {
          (libraryStore.filteredProjects.length === 0 ? (
            <h3 className="p-5 m-5">No projects available</h3>
          ) : (
            <ProjectCards />
          ))}
      </div>

      {/* Pagination */}
      {libraryStore.filteredProjects.length > 0 && (
        <Pagination
          siblingCount={2}
          className="pagination-bar"
          currentPage={libraryStore.pageNumber}
          totalCount={libraryStore.filteredProjects.length}
          pageSize={libraryStore.limitPerPage}
          onPageChange={(page) => vm.setCurrentPage(page)}
        />
      )}

      {libraryStore.advancedSearchIsOpen && <AdvancedSearch />}
    </div>
  );
};

export const ProjectsLibrary = observer(ProjectsComponent);

const ProjectCards = () => {
  const from = Number(libraryStore.pageNumber - 1) * libraryStore.limitPerPage;
  const to = from + Number(libraryStore.limitPerPage);
  const projectsOnPage = libraryStore.filteredProjects.slice(from, to);

  return (
    <>
      {projectsOnPage.map((project: IGetAllProjectsObject) => (
        <ProjectCard key={project.projectUuid} project={project} />
      ))}
    </>
  );
};

const AppliedFilterButton = (props) => {
  return (
    <button
      data-testid={props.name}
      className="usa-button usa-button--outline rounded-btn mb-2"
      onClick={props.onClick}
    >
      {props.name} <Icon path={mdiClose} size={1} />
    </button>
  );
};
